import {Injectable} from '@angular/core';
import {concatMap, Observable} from 'rxjs';
import {createStore, select, setProp, withProps} from "@ngneat/elf";
import {APIService} from "./api.service";
import {getAllEntities, selectAllEntities, setEntities, withEntities} from "@ngneat/elf-entities";
import {IGameQueryResult} from "../interfaces/IGameQueryResult";
import {withRequestsCache} from "@ngneat/elf-requests";
import {tap} from "rxjs/operators";
import {IRecalculateGameEntriesResult} from "../interfaces/IRecalculateGameEntriesResult";
import {IRecalculateGameEntriesCommand} from "../interfaces/IRecalculateGameEntriesCommand";
import {IGroupedRecalculationEntries} from "../interfaces/IGroupedRecalculationEntries";
import {IPaymentTier} from "../interfaces/player/IPaymentTier";
import {GameStateEnum} from "../enum/GameStateEnum";
import {IAddPaymentTiersToGame} from "../interfaces/IAddPaymentTiersToGame";
import {IAddNewPaymentTier} from "../interfaces/IAddNewPaymentTier";
import {IEditGameDescription} from "../interfaces/IEditGameDescription";
import {IEditGameMoreInformation} from "../interfaces/IEditGameMoreInformation";
import {IEditCharityRegistrationUrl} from "../interfaces/IEditCharityRegistrationUrl";
import {ICreateAzureResourcesForGameResult} from "../interfaces/ICreateAzureResourcesForGameResult";
import {ICreateAzureResourcesForGameCommand} from "../interfaces/ICreateAzureResourcesForGameCommand";
import {IUploadImageToAzure} from "../interfaces/IUploadImageToAzure";
import {IUpdateNotificationSettings} from "../interfaces/IUpdateNotificationSettings";
import {IPauseGame} from "../interfaces/IPauseGame";
import {IUpdateGameSendLastFourDigitsOfWinningCard} from "../interfaces/IUpdateGameSendLastFourDigitsOfWinningCard";
import {IForceDrawWinner} from "../interfaces/IForceDrawWinner";
import {IStopGame} from "../interfaces/IStopGame";
import {IEndGame} from "../interfaces/IEndGame";
import {IUpdateUnpauseDate} from "../interfaces/IUpdateUnpauseDate";
import {IPlayerEmail} from "../interfaces/IPlayerEmail";

@Injectable({
  providedIn: 'root'
})
export class GameService {

  private gameStore = createStore(
    {name: 'game'},
    withProps<{ activeGame: IGameQueryResult | null }>({
      activeGame: null
    }),
    withRequestsCache<'game-cache'>()
  );

  private potentialGamesStore = createStore(
    {name: 'all-games-state'},
    withEntities<IGameQueryResult, 'Id'>({idKey: 'Id'}),
    withRequestsCache<'all-games'>()
  );

  private gamePaymentTiersStore = createStore(
    {name: 'game-payment-tiers'},
    withEntities<IPaymentTier, 'Id'>({idKey: 'Id'})
  );

  constructor(private apiService: APIService) {
  }

  public getActiveGamePaymentTiers(): IPaymentTier[] {
    return this.gameStore.query((store) => store.activeGame ? store.activeGame?.PaymentTiers : []);
  }

  public getAllPaymentTiers(gameIdP: string): Observable<IPaymentTier[]> {
    return this.apiService.MakeGetRequest<IPaymentTier[]>(`game/payment-tiers/${gameIdP}`).pipe(
      tap(tiers => this.gamePaymentTiersStore.update(setEntities(tiers)))
    );
  }

  public getPaymentTiersFromStore(): IPaymentTier[] {
    return this.gamePaymentTiersStore.query(getAllEntities());
  }

  public addPaymentTierToGame(newTierRequest: IAddPaymentTiersToGame): Observable<IGameQueryResult> {
    return this.apiService.MakePostRequest(`game/payment-tiers-to-game`, newTierRequest).pipe(concatMap(() => {
      return this.getGameByGameId(newTierRequest.gameId);
    }));
  }

  public addNewPaymentTier(newTierRequest: IAddNewPaymentTier): Observable<any> {
    return this.apiService.MakePostRequest(`game/payment-tier`, newTierRequest);
  }

  public getGamesForUser(adminIdP: string): Observable<IGameQueryResult[]> {
    return this.apiService.MakeGetRequest<IGameQueryResult[]>(`game/all-for-user/${adminIdP}`).pipe(tap((gamesByGroup) => {
      this.potentialGamesStore.update(setEntities(gamesByGroup));
    }));
  }

  public getGameByGameId(gameIdP: string): Observable<IGameQueryResult> {
    return this.apiService.MakeGetRequest<IGameQueryResult>(`game/${gameIdP}`).pipe(tap(
      (game) => this.gameStore.update(setProp("activeGame", game))));
  }

  public activeGame(): IGameQueryResult {
    return this.gameStore.query(state => state.activeGame!);
  }

  public clearActiveGame() {
    this.gameStore.reset();
  }

  public selectActiveGameId(): Observable<string> {
    return this.gameStore.pipe(select((state) => state.activeGame?.Id!));
  }

  public selectActiveGameDrawnManually(): Observable<boolean> {
    return this.gameStore.pipe(select((state) => state.activeGame?.ManualDraw ?? false));
  }

  public selectActiveGame$(): Observable<IGameQueryResult | undefined> {
    return this.gameStore.pipe(select((state) => state.activeGame!));
  }

  public updateActiveGame(gameP: IGameQueryResult) {
    this.gameStore.update(setProp("activeGame", gameP));
  }

  public getAllAvailableGames(): IGameQueryResult[] {
    return this.potentialGamesStore.query(getAllEntities());
  }

  public getAllDraftGames(): IGameQueryResult[] {
    return this.potentialGamesStore.query(getAllEntities()).filter((game) => game.State == GameStateEnum.Draft);
  }

  public selectAllAvailableGames(): Observable<IGameQueryResult[]> {
    return this.potentialGamesStore.pipe(selectAllEntities());
  }

  public recalculateGame(gameIdP: string): Observable<IGroupedRecalculationEntries[]> {
    return this.apiService.MakeGetRequest<IGroupedRecalculationEntries[]>(`game/recalculate/${gameIdP}`);
  }

  public createAzureResourcesForGame() {
    const command: ICreateAzureResourcesForGameCommand = {
      gameId: this.activeGame().Id
    };

    return this.apiService.MakePutRequest<ICreateAzureResourcesForGameResult>(`game/create-azure-resources-for-game/`, command);
  }

  public commitRecalculationChanges(gameIdP: string): Observable<IRecalculateGameEntriesResult> {
    let command: IRecalculateGameEntriesCommand = {
      gameId: gameIdP
    };

    return this.apiService.MakePutRequest<IRecalculateGameEntriesResult>(`game/commit-recalculation-updates`, command);
  }

  public generateAssetUrl(desiredAssetP: string, cdnRoot: string) {
    return `${cdnRoot}/assets/${this.activeGame().Subdomain}/${desiredAssetP}`;
  }

  public updateGameDescription(commandP: IEditGameDescription) {
    return this.apiService.MakePutRequest("game/update-description", commandP);
  }

  public updateGameMoreInformation(commandP: IEditGameMoreInformation) {
    return this.apiService.MakePutRequest("game/update-more-information", commandP);
  }

  public updateCharityRegistrationUrl(commandP: IEditCharityRegistrationUrl) {
    return this.apiService.MakePutRequest("game/update-charity-registration-url", commandP);
  }

  public uploadGameImage(uploadLogoRequestP: IUploadImageToAzure) {
    return this.apiService.uploadFile(`game/upload-cdn-image/${uploadLogoRequestP.gameId}/${uploadLogoRequestP.adminId}`, uploadLogoRequestP.image);
  }

  updateGameNotificationSettings(command: IUpdateNotificationSettings) {
    return this.apiService.MakePutRequest('game/update-notification-settings', command);
  }

  pauseGame(commandP: IPauseGame) {
    return this.apiService.MakePostRequest("game/pause", commandP).pipe(concatMap(() =>
      this.getGameByGameId(commandP.GameId)
    ));
  }

  updateUnpauseDate(commandP: IUpdateUnpauseDate) {
    return this.apiService.MakePutRequest("game/update-unpause-date", commandP).pipe(concatMap(() =>
      this.getGameByGameId(commandP.GameId)
    ));
  }

  stopGame(commandP: IStopGame) {
    return this.apiService.MakePostRequest("game/stop", commandP).pipe(concatMap(() =>
      this.getGameByGameId(commandP.GameId)
    ));
  }

  endGame(commandP: IEndGame) {
    return this.apiService.MakePostRequest("game/end", commandP).pipe(concatMap(() =>
      this.getGameByGameId(commandP.GameId)
    ));
  }

  updateGameSendLastFourDigitsOfWinningCard(commandP: IUpdateGameSendLastFourDigitsOfWinningCard) {
    return this.apiService.MakePostRequest("game/update-send-last-four-digits-of-winning-card", commandP).pipe(concatMap(() =>
      this.getGameByGameId(commandP.GameId)));
  }

  updateGameForceWinner(commandP: IForceDrawWinner) {
    return this.apiService.MakePostRequest("game/force-draw-winner", commandP).pipe(concatMap(() =>
      this.getGameByGameId(commandP.GameId)));
  }

  getActivePlayerEmails(gameIdP: string): Observable<IPlayerEmail[]> {
    return this.apiService.MakeGetRequest<IPlayerEmail[]>(`game/${gameIdP}/active-player-emails`);
  }
}
